/**
 * ACTIVITY TIMELINE — Componente condiviso per timeline attivita'.
 *
 * Usato in: PersonDetail, BusinessDetail, QuotationDetail, DashboardPage.
 * Mostra una lista cronologica di attivita' raggruppate per data,
 * con icona tipo, titolo, dettaglio e timestamp.
 */

import { useState, useMemo, useEffect, useLayoutEffect, useRef, Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  MailGlyph,
  PhoneGlyph,
  CalendarGlyph,
  EditGlyph,
  CheckSquareGlyph,
  SettingsGlyph,
  FolderGlyph,
} from './icons';
import { formatDateTime } from '@crm/shared';
import { useAppDispatch } from '../store/hooks';
import { getEntityFqn } from '../entityRegistry/entityRegistry';
import { useToast } from './useToast';
import {
  createBidirectionalRelationship,
  setRelationshipPinned,
} from '../entities/relationship/relationshipThunks';
import { compareActivityDateDesc, WHATSAPP_SESSION_FQN } from './useEntityActivities';
import { deleteTask, updateTask, updateTaskStatus } from '../entities/task/taskThunks';
import { deleteNote } from '../entities/note/noteThunks';
import { deleteCall } from '../entities/call/callThunks';
import { deleteEmail } from '../entities/email/emailThunks';
import { deleteMeeting } from '../entities/meeting/meetingThunks';
import { deleteLinkedinPost } from '../entities/linkedinPost/linkedinPostThunks';
import { deleteWhatsAppSession } from '../entities/whatsAppSession/whatsAppSessionThunks';
import { ConfirmDialog } from './ConfirmDialog';
import { useConfirmDialog } from './useConfirmDialog';
import { ACTIVITY_TAB_KINDS, sectionToActiveFilters } from './activitySections';
import { SLOT_ORDER_LAST } from '../plugin/useSlotContributors';
import type { SlotContributor } from '@crm/contracts';
import { TaskStatusIcon } from '../entities/task/TaskStatusIcon';
import { TaskCompletionToggle } from '../entities/task/TaskCompletionToggle';
import { isTaskExpired } from '../entities/task/taskStatus';
import { EditableField, type EditableValue } from './EditableField';
import { TaskEditFormBody } from '../entities/task/TaskEditFormBody';
import { NoteEditFormBody } from '../entities/note/NoteEditFormBody';
import { CallEditFormBody } from '../entities/call/CallEditFormBody';
import { EmailEditFormBody } from '../entities/email/EmailEditFormBody';
import { MeetingEditFormBody } from '../entities/meeting/MeetingEditFormBody';
import { LinkedinPostEditFormBody } from '../entities/linkedinPost/LinkedinPostEditFormBody';
import { MessageActivityBody } from '../entities/message/MessageActivityBody';
import type { Task, TaskStatus } from '../entities/task/taskTypes';
import type { Note } from '../entities/note/noteTypes';
import type { Call } from '../entities/call/callTypes';
import type { Email } from '../entities/email/emailTypes';
import type { Meeting } from '../entities/meeting/meetingTypes';
import type { LinkedinPost } from '../entities/linkedinPost/linkedinPostTypes';
import type { MessageSummary } from '../entities/message/messageTypes';
import type { WhatsAppSession } from '../entities/whatsAppSession/whatsAppSessionTypes';
import { WhatsAppSessionChat } from '../entities/whatsAppSession/WhatsAppSessionChat';

/**
 * Titolo readonly di un'attività nella summary dell'accordion.
 *
 * In modalità `multiline` (usata per le attività "descrizione-come-titolo":
 * email/chiamata/riunione/linkedin da chiuso, senza "Primo contatto") mostra
 * il testo originale su max 2 righe. Se il testo eccede le 2 righe, aggiunge la
 * classe `--faded` che sfuma la 2ª riga (via mask CSS) per segnalare che c'è
 * altro testo e va aperto per leggerlo. La sfumatura NON appare quando il testo
 * sta in 2 righe: l'overflow è misurato via ResizeObserver (regge i resize del
 * pannello). Fuori da `multiline` il titolo è un titolo vero (oggetto del
 * messaggio, nota, "Primo contatto - X") e da CHIUSO si ferma a 2 righe con
 * l'ellipsis, per tenere allineata la pila delle card; da ESPANSO invece si
 * mostra per intero, perché è l'informazione che si sta andando a leggere.
 * L'anteprima `multiline` resta tagliata anche da espansa: lì il testo completo
 * è già nel corpo dell'accordion, ripeterlo nell'header sarebbe un doppione.
 */
function ClampedTitle({
  text,
  multiline,
  completed,
  expanded,
}: {
  text: string;
  multiline: boolean;
  completed: boolean;
  expanded: boolean;
}) {
  const ref = useRef<HTMLElement>(null);
  const [faded, setFaded] = useState(false);
  useLayoutEffect(() => {
    if (!multiline) {
      setFaded(false);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const check = () => setFaded(el.scrollHeight - el.clientHeight > 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, multiline]);
  const className = [
    'activity-accordion__title',
    multiline && 'activity-accordion__title--multiline',
    faded && 'activity-accordion__title--faded',
    completed && 'activity-accordion__title--completed',
    expanded && !multiline && 'activity-accordion__title--expanded',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <strong ref={ref} className={className}>
      {text}
    </strong>
  );
}

/** Un contatto coinvolto in un'attività, per la riga "con {nomi}" nell'header. */
export interface ActivityRecipient {
  personId: number;
  name: string;
  /** true se è il contatto della detail page corrente: mostrato ma non linkabile. */
  self: boolean;
}

export interface ActivityItem {
  id: string | number;
  /** `contributo`: un elemento di una tab contribuita da un plugin (`loadActivities`). */
  type: 'nota' | 'email' | 'chiamata' | 'riunione' | 'linkedin' | 'whatsapp' | 'task' | 'sistema' | 'file' | 'contributo';
  title: string;
  detail?: string;
  user: string;
  date: string;
  /** Identità leggera dell'attività (FQN + primary key), popolata dalla timeline
   *  bulk (useEntityActivities). Permette pin, "con {nomi}" e fetch lazy del record
   *  pieno on-expand senza dipendere dal record completo (assente finché chiuso). */
  className?: string;
  classPK?: number;
  status?: TaskStatus;
  expirationDate?: string | null;
  /** Persona piu' vecchia collegata al task via Relationship.
   *  Popolata solo per type !== 'nota'; renderizzata come "Primo contatto - {name}". */
  firstContactName?: string;
  /** Contatti (Person) collegati all'attività: i "con {nomi}" mostrati
   *  nell'header solo da esteso. Popolata per email, chiamate, post LinkedIn e
   *  riunioni (queste ultime solo se senza titolo backend). Ogni recipient
   *  diverso da quello della pagina corrente (self=false) è reso come link
   *  cliccabile al proprio dettaglio. */
  recipients?: ActivityRecipient[];
  /** Id della Relationship entita'->attivita' (sorgente = entita' corrente):
   *  e' il record su cui agire per il pin (vedi useEntityActivities). Assente
   *  se l'attivita' non ha un record entita'-sorgente in cache. */
  relationshipId?: number;
  /** Stato pin di quella Relationship: l'attivita' e' "appuntata" (importante)
   *  rispetto alle altre dell'entita'. Al piu' 1 appuntata per entita'. */
  pinned?: boolean;
  /** Discriminator entita': se l'item deriva da un Task, `task` e' valorizzato;
   *  se da una Note, `note`; se da una Call, `call`; se da un Meeting,
   *  `meeting`; se da una Email, `email`; se da un Message (email importata),
   *  `message`. Mutualmente esclusivi. */
  task?: Task;
  note?: Note;
  call?: Call;
  email?: Email;
  meeting?: Meeting;
  linkedinPost?: LinkedinPost;
  /** Message (email importata dalla casella): proiezione LEGGERA per la sezione Email.
   *  Reso come accordion espandibile read-only (MessageActivityBody); il Message pieno
   *  vive nella smart inbox. */
  message?: MessageSummary;
  whatsAppSession?: WhatsAppSession;
  /** Solo per `contributo`: la tab che l'ha portato, e il suo id per `activityRow`. */
  contributor?: SlotContributor;
  contributedId?: number;
}

interface ActivityTimelineProps {
  items: ActivityItem[];
  filters?: readonly string[];
  /**
   * Sezione corrente, cioè lo SLUG nell'URL (`/contacts/1/notes`). `undefined` =
   * "Tutte". La striscia lavora in slug e non in filtri (TS-107): una tab
   * contribuita da un plugin non è un filtro su una lista di attività, e finché
   * il tipo era la moneta di scambio non poteva starci dentro. Il filtro sugli
   * item lo deriva la timeline da sé — prima lo faceva la scheda, per poi
   * riconvertirlo in slug al primo click.
   */
  section?: string;
  /** Sezione scelta dall'utente; `undefined` = "Tutte". Il parent naviga. */
  onSectionChange?: (section: string | undefined) => void;
  /**
   * Tab contribuite dai plugin per questa entità (slot
   * `detail-center:activity-tab`). Le passa la scheda, che le legge dal registry:
   * la timeline non conosce i plugin, riceve una lista di posizioni e componenti.
   */
  activityTabs?: readonly SlotContributor[];
  /** Id dell'entità ospite, passato al componente della tab contribuita. */
  entityId?: number;
  emptyMessage?: string;
  /**
   * Se true, mostra un campo ricerca testuale sopra i filtri tipo.
   * Default false (usato solo nei detail che lo richiedono esplicitamente,
   * es. PersonDetail). In Dashboard il filtro testuale non serve.
   */
  searchable?: boolean;
  /**
   * Se definito, mostra un bottone "+ {label}" a destra delle Tabs quando
   * un filtro (diverso da "Tutte") e' attivo. Il parent ritorna
   * {label, onClick} o null se quel tipo non e' creabile.
   * Usato da PersonDetail/BusinessDetail per aprire il TaskForm con
   * initialType opportuno.
   */
  /**
   * Se il parent fornisce un'azione di creazione per il tipo selezionato,
   * la timeline mostra il bottone "+ Nuova X" in alto. Ritorno opzionale:
   * `disabled` (con `disabledReason` come tooltip) per voci "in arrivo" —
   * il bottone resta visibile ma non cliccabile.
   */
  getCreateAction?: (
    type: ActivityItem['type'],
  ) => { label: string; onClick: () => void; disabled?: boolean; disabledReason?: string } | null;
  /**
   * Id dell'item su cui atterrare (deep-link commento): l'accordion viene espanso
   * e portato in vista. Confronto coercitivo (il Task usa id numerico, l'URL una
   * stringa). One-shot: agisce alla prima volta che l'item è disponibile.
   */
  focusItemId?: string | number | null;
  /**
   * Invocata quando un'attività viene espansa. Usata per il fetch LAZY dei
   * contatti "con {nomi}" (vedi useEntityActivities.requestRecipients): i
   * recipient si risolvono solo all'espansione, non al load della timeline.
   * L'implementazione deve essere idempotente (chiamata più volte per lo stesso
   * item senza rifetch).
   */
  onExpandItem?: (item: ActivityItem) => void;
}

// Emblema della timeline: icona + colore d'accento per tipo attività. Il colore
// riusa le CSS custom property `--color-chip-*` (unica fonte, condivisa coi dot).
const TYPE_ICON: Record<ActivityItem['type'], ReactNode> = {
  nota: <EditGlyph />,
  email: <MailGlyph />,
  chiamata: <PhoneGlyph />,
  riunione: <CalendarGlyph />,
  linkedin: <LinkedInIcon />,
  whatsapp: <WhatsAppIcon />,
  task: <CheckSquareGlyph />,
  sistema: <SettingsGlyph />,
  file: <FolderGlyph />,
  contributo: <FolderGlyph />,
};
/**
 * Un ActivityItem è "scaduto" (ai fini visuali/ordinamento) se è un task non
 * completato con scadenza passata. Delega a isTaskExpired (logica condivisa con
 * TaskList); qui aggiunge solo il fatto che l'item porti un task.
 */
function isItemExpired(item: ActivityItem): boolean {
  return !!item.task && isTaskExpired(item.task);
}

export function ActivityTimeline({
  items,
  filters,
  section,
  onSectionChange,
  activityTabs,
  entityId,
  emptyMessage,
  searchable = false,
  getCreateAction,
  focusItemId,
  onExpandItem,
}: ActivityTimelineProps) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const pinConfirm = useConfirmDialog();
  const deleteConfirm = useConfirmDialog();
  const [search, setSearch] = useState('');
  // Set degli accordion aperti. Controllato: permette il toggle globale
  // "Espandi / Comprimi tutto" accanto alla search.
  const [expandedIds, setExpandedIds] = useState<Set<string | number>>(new Set());

  // Gli elementi delle tab contribuite che dichiarano `loadActivities`: danno il numero
  // alla tab ed entrano in «Tutte». Si ricaricano al cambio di sezione, così quello che
  // si modifica dentro la tab si ritrova in «Tutte». Un caricatore che fallisce non
  // porta elementi: l'errore lo mostra la tab stessa, quando la si apre.
  const [contributed, setContributed] = useState<ActivityItem[]>([]);
  useEffect(() => {
    const loaders = (activityTabs ?? []).filter((c) => c.slug && c.loadActivities);
    if (!entityId || loaders.length === 0) {
      setContributed([]);
      return;
    }
    let alive = true;
    Promise.all(
      loaders.map((c) =>
        c.loadActivities!(entityId).then(
          (rows): ActivityItem[] =>
            rows.map((r) => ({
              id: `${c.slug}:${r.id}`,
              type: 'contributo',
              title: r.title,
              detail: r.detail,
              user: r.user ?? '',
              date: r.date,
              contributor: c,
              contributedId: r.id,
            })),
          (): ActivityItem[] => [],
        ),
      ),
    ).then((lists) => {
      if (alive) setContributed(lists.flat());
    });
    return () => {
      alive = false;
    };
  }, [activityTabs, entityId, section]);

  // Attività del core e contribuite insieme, per data decrescente come le ordina
  // useEntityActivities (ordinamento stabile: fra pari data resta l'ordine del core).
  const allItems = useMemo(
    () =>
      contributed.length === 0
        ? items
        : [...items, ...contributed].sort(compareActivityDateDesc),
    [items, contributed],
  );

  // Deep-link commento: espando l'attività target. Lo scroll preciso al commento
  // lo fa ActivityTools (un solo smooth-scroll: due si annullerebbero a vicenda).
  // Riparte se cambia focusItemId (navigazione a un altro deep-link).
  const focusHandledRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (focusItemId == null || focusHandledRef.current === focusItemId) return;
    // Confronto coercitivo: item.id del Task è numerico, focusItemId dall'URL stringa.
    const target = allItems.find((it) => String(it.id) === String(focusItemId));
    if (!target) return; // item non ancora caricato o assente: riprovo al prossimo render
    focusHandledRef.current = focusItemId;
    setExpandedIds((prev) => new Set(prev).add(target.id));
  }, [focusItemId, allItems]);

  // Notifica il parent di ogni item espanso: alimenta il fetch lazy dei "con
  // {nomi}" (requestRecipients). Copre tutte le vie di espansione (toggle
  // singolo, "espandi tutto", deep-link). Idempotente lato hook, quindi safe
  // richiamarlo a ogni cambio di expandedIds/items.
  useEffect(() => {
    if (!onExpandItem) return;
    for (const id of expandedIds) {
      const it = items.find((i) => i.id === id);
      if (it) onExpandItem(it);
    }
  }, [expandedIds, items, onExpandItem]);

  // Menu "Azioni" del singolo pannello: anchor + id dell'item a cui si
  // riferisce. Visibile solo quando l'accordion e' espanso.
  const [actionsMenu, setActionsMenu] = useState<{ anchorEl: HTMLElement; itemId: string | number } | null>(null);
  const query = search.trim().toLowerCase();

  // Apertura/chiusura menu azioni. stopPropagation: il bottone vive dentro
  // l'AccordionSummary, senza fermare l'evento il click farebbe togglare
  // (collassare) il pannello invece di aprire il menu.
  function openActions(e: React.MouseEvent<HTMLElement>, itemId: string | number) {
    e.stopPropagation();
    setActionsMenu({ anchorEl: e.currentTarget, itemId });
  }
  function closeActions() {
    setActionsMenu(null);
  }
  // Item a cui si riferisce il menu aperto (per label/icona dinamiche e pin).
  const activeItem = actionsMenu ? items.find((it) => it.id === actionsMenu.itemId) : undefined;
  const activePinned = activeItem?.pinned ?? false;

  // Esegue il toggle pin sulla Relationship entita'->attivita' (TS-22). Il
  // backend garantisce "al piu' 1 pinnata per sorgente"; lo slice replica il
  // demote locale. Niente refetch: la lista derivata si rigenera da state.
  async function doPin(relationshipId: number, pinned: boolean) {
    try {
      await dispatch(setRelationshipPinned({ relationshipId, pinned })).unwrap();
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  /**
   * Relazione su cui agire per il pin. Normalmente c'e' gia': e' come l'attivita' e'
   * finita sulla scheda del contatto.
   *
   * FALLBACK per le sessioni WhatsApp: quelle si legano al contatto con la FK
   * `personId`, e la riga di relazione la crea il backend dal momento in cui e' stata
   * introdotta. Le sessioni piu' vecchie non ce l'hanno, quindi la si crea qui, alla
   * prima appuntatura — invece di lasciare l'azione rotta su tutto lo storico.
   * `addRelationship` lato server e' idempotente, quindi non si rischiano doppioni.
   */
  async function ensureRelationshipId(item: ActivityItem): Promise<number | undefined> {
    if (item.relationshipId) return item.relationshipId;

    const session = item.whatsAppSession;
    const personFqn = getEntityFqn('Person');
    if (!session || session.personId <= 0 || !personFqn) return undefined;

    const created = await dispatch(
      createBidirectionalRelationship({
        className1: personFqn,
        classPK1: session.personId,
        className2: WHATSAPP_SESSION_FQN,
        classPK2: session.whatsAppSessionId,
        descriptionForward: null,
        descriptionReverse: null,
      }),
    ).unwrap();

    // La forward e' quella che porta il pin: sorgente = il contatto.
    return created.find(
      (r) => r.className1 === personFqn && r.classPK1 === session.personId,
    )?.relationshipId;
  }

  // Appunta/Sblocca. Se si sta appuntando una NUOVA attivita' ma ne esiste gia'
  // un'altra appuntata per questo contatto, chiede conferma: il vincolo backend
  // e' "1 sola pinnata per sorgente", quindi appuntare questa sostituirebbe
  // l'altra. L'utente sceglie se sostituire o mantenere l'attuale.
  async function handlePin() {
    const item = activeItem;
    closeActions();
    if (!item) return;

    let relationshipId: number | undefined;
    try {
      relationshipId = await ensureRelationshipId(item);
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      return;
    }
    if (!relationshipId) {
      toast({ severity: 'warning', message: t('activity.actions.pinUnavailable') });
      return;
    }
    const relId = relationshipId;
    // Unpin: nessun conflitto, procedi diretto.
    if (item.pinned) {
      void doPin(relId, false);
      return;
    }
    // Pin di una nuova: c'e' gia' un'altra attivita' pinnata per il contatto?
    const existing = items.find((it) => it.pinned && it.id !== item.id);
    if (existing) {
      pinConfirm.requestConfirm(
        t('activity.actions.pinReplaceMessage', { title: existing.title }),
        () => void doPin(relId, true),
      );
      return;
    }
    void doPin(relId, true);
  }

  // Elimina il record dell'attivita' (Task/Note/Call/Email/Meeting) tramite il
  // delete thunk dell'entita' corrispondente. Il slice rimuove da byId e la
  // lista derivata (useEntityActivities) si rigenera senza l'item.
  async function doDelete(item: ActivityItem) {
    try {
      if (item.task) await dispatch(deleteTask(item.task.taskId)).unwrap();
      else if (item.note) await dispatch(deleteNote(item.note.noteId)).unwrap();
      else if (item.call) await dispatch(deleteCall(item.call.callId)).unwrap();
      else if (item.email) await dispatch(deleteEmail(item.email.emailId)).unwrap();
      else if (item.meeting) await dispatch(deleteMeeting(item.meeting.meetingId)).unwrap();
      else if (item.linkedinPost) await dispatch(deleteLinkedinPost(item.linkedinPost.linkedinPostId)).unwrap();
      else if (item.whatsAppSession) await dispatch(deleteWhatsAppSession(item.whatsAppSession.whatsAppSessionId)).unwrap();
      else return;
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  // Commit del titolo task editato inline nell'header dell'accordion (sostituisce
  // il vecchio campo titolo duplicato nel body). Rigetta su errore: EditableField
  // ha bisogno della reject per mostrare lo stato di errore e non uscire da edit.
  async function commitTaskTitle(task: Task, newValue: EditableValue) {
    try {
      await dispatch(updateTask({ taskId: task.taskId, title: newValue as string })).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      throw err;
    }
  }

  // Toggle "completato" del task dal pallino accanto al titolo: completato <->
  // da completare. Lo status viaggia per l'endpoint dedicato (updateTaskStatus);
  // lo slice aggiorna byId, la lista derivata si rigenera (l'item esce/rientra
  // dalla sezione "Imminente" in base allo stato).
  async function toggleTaskCompleted(task: Task) {
    const next: TaskStatus = task.status === 'completed' ? 'todo' : 'completed';
    try {
      await dispatch(updateTaskStatus({ taskId: task.taskId, status: next })).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  // Elimina: chiede conferma (azione distruttiva) e poi rimuove il record.
  function handleDelete() {
    const item = activeItem;
    closeActions();
    if (!item) return;
    deleteConfirm.requestConfirm(
      t('activity.actions.deleteConfirm'),
      () => void doDelete(item),
    );
  }

  // Tab della striscia: «Tutte», quelle core (i tipi filtrabili) e quelle contribuite dai
  // plugin, in una lista sola ordinata per `order`. I tipi core sono numerati di
  // cento in cento in activitySections, quindi un contributo si intercala.
  //
  // Anche «Tutte» sta nella lista, a ordine 0: era un `<Tab>` scritto a mano prima del ciclo,
  // e finché è rimasta lì niente poteva precederla. Un contributo con ordine negativo — i
  // «Dati della pratica» di Pollini — ora si mette davanti senza che il core sappia di lui.
  const stripTabs = [
    {
      value: '',
      type: null as string | null,
      order: 0,
      label: t('activity.allTypes'),
      contributor: null as SlotContributor | null,
    },
    ...ACTIVITY_TAB_KINDS
      .filter((k) => !filters || filters.includes(k.type))
      .map((k) => ({
        value: k.slug,
        type: k.type as string | null,
        order: k.order,
        label: k.type.charAt(0).toUpperCase() + k.type.slice(1),
        contributor: null as SlotContributor | null,
      })),
    ...(activityTabs ?? [])
      .filter((c) => c.slug)
      .map((c) => ({
        value: c.slug as string,
        type: null as string | null,
        order: c.order ?? SLOT_ORDER_LAST,
        label: c.labelKey ? t(c.labelKey) : c.key,
        contributor: c as SlotContributor | null,
      })),
  ].sort((a, b) => a.order - b.order);

  // Sezione attiva. Uno slug che non è fra le tab — 'linkedin', che ha una rotta
  // ma non un pill — vale "Tutte", come prima.
  const activeSection = stripTabs.some((tab) => tab.value === section) ? section : undefined;
  const activeContributor =
    stripTabs.find((tab) => tab.value === activeSection)?.contributor ?? null;

  // Il filtro per tipo si deriva dalla sezione: la scheda passa lo slug, non i tipi.
  const activeFilters = sectionToActiveFilters(activeSection);

  // Doppio filtro: prima per tipo (dalla sezione attiva), poi per testo
  // (search, se il campo e' abilitato e valorizzato).
  const displayed = allItems
    .filter((item) => !activeFilters?.length || activeFilters.includes(item.type))
    .filter((item) => {
      if (!query) return true;
      return (
        item.title.toLowerCase().includes(query) ||
        (item.detail?.toLowerCase().includes(query) ?? false) ||
        item.user.toLowerCase().includes(query)
      );
    });

  // Conteggio per tipo per il badge accanto a ogni chip filtro. Calcolato sui
  // match della SOLA ricerca (non del filtro tipo attivo), così ogni chip mostra
  // quante attività di quel tipo esistono nel set corrente; "Tutte" = totale.
  const searchMatched = allItems.filter(
    (item) =>
      !query ||
      item.title.toLowerCase().includes(query) ||
      (item.detail?.toLowerCase().includes(query) ?? false) ||
      item.user.toLowerCase().includes(query),
  );
  const totalCount = searchMatched.length;
  const countByType = new Map<string, number>();
  const countByContributor = new Map<string, number>();
  for (const it of searchMatched) {
    countByType.set(it.type, (countByType.get(it.type) ?? 0) + 1);
    if (it.contributor) {
      countByContributor.set(it.contributor.key, (countByContributor.get(it.contributor.key) ?? 0) + 1);
    }
  }

  // Il badge col numero si attacca qui e non sopra: i conteggi nascono dal set
  // filtrato per ricerca, che a sua volta dipende da `displayed`.
  const stripTabsWithCounts = stripTabs.map((tab) => ({
    ...tab,
    // «Tutte» porta il totale, i tipi il proprio conteggio. Una contribuita ha il numero
    // solo se dichiara `loadActivities`: senza, non conta elementi, mostra altro.
    count: tab.contributor
      ? tab.contributor.loadActivities
        ? countByContributor.get(tab.contributor.key) ?? 0
        : null
      : tab.type === null
        ? totalCount
        : countByType.get(tab.type) ?? 0,
  }));

  // Task scaduti non completati: in cima a TUTTO, sotto la label "Imminente".
  // Vanno visti subito perche' urgenti; appena completati escono di qui e
  // rientrano nel loro gruppo-mese cronologico (isItemExpired -> false).
  const expiredItems = displayed.filter((it) => isItemExpired(it));

  // Attivita' appuntate (pinned): mostrate in cima, dopo gli scaduti, fuori dai
  // gruppi mese. Al piu' 1 per entita' (vincolo backend), ma gestiamo un array
  // per robustezza. Uno scaduto+pinned va nella sezione scaduti (precedenza).
  const pinnedItems = displayed.filter((it) => it.pinned && !isItemExpired(it));

  // Raggruppamento per mese/anno. L'ordine dei gruppi preserva quello
  // di `displayed` (che arriva gia' ordinato dal hook a monte), quindi
  // se items e' desc avremo mesi dal piu' recente al piu' vecchio.
  // Le attivita' pinned e gli scaduti sono esclusi: renderizzati a parte in cima.
  //
  // Le attivita' SENZA DATA finiscono in un gruppo a parte, in fondo (FILO-48). Prima venivano
  // saltate: il chip le contava, l'elenco no, e in produzione una pratica mostrava «Email (3)» e
  // nessuna email — i messaggi importati via MCP senza data di ricezione.
  const monthGroups = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(i18n.language, { year: 'numeric', month: 'long' });
    const groups: { label: string; items: ActivityItem[] }[] = [];
    const undated: ActivityItem[] = [];
    for (const item of displayed.filter((it) => !it.pinned && !isItemExpired(it))) {
      const d = new Date(item.date);
      if (Number.isNaN(d.getTime())) {
        undated.push(item);
        continue;
      }
      const label = fmt.format(d);
      const last = groups[groups.length - 1];
      if (last && last.label === label) last.items.push(item);
      else groups.push({ label, items: [item] });
    }
    if (undated.length > 0) groups.push({ label: t('activity.noDateHeading'), items: undated });
    return groups;
  }, [displayed, i18n.language, t]);

  // Tutti aperti / tutti chiusi: il toggle bottone setta/svuota il Set.
  const allExpanded = displayed.length > 0 && displayed.every((it) => expandedIds.has(it.id));
  function toggleAll() {
    if (allExpanded) setExpandedIds(new Set());
    else setExpandedIds(new Set(displayed.map((it) => it.id)));
  }

  function toggleOne(id: string | number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Render di una singola attivita' (Accordion). Estratto per riusarlo sia
  // per le pinned (in cima) sia per i gruppi mese.
  function renderActivity(item: ActivityItem) {
    const isNote = item.type === 'nota';
    // Elemento di una tab contribuita con la sua riga (`activityRow`): la disegna il plugin,
    // con la stessa card della tab. Senza, è un accordion come gli altri: titolo (già
    // tradotto dal plugin) in grassetto, dettaglio sotto, niente menu Azioni, che agisce su
    // record del core.
    const isContributed = item.type === 'contributo';
    const ContributedRow = item.contributor?.activityRow;
    if (ContributedRow && item.contributedId != null) {
      return <ContributedRow key={item.id} entityId={entityId ?? 0} activityId={item.contributedId} />;
    }
    // Message = email importata: attività a pieno titolo nella sezione Email (accordion
    // espandibile come le altre), ma con corpo read-only. Passa dallo stesso percorso di
    // render; le differenze (etichetta "Messaggio", titolo = oggetto, niente Elimina) sono
    // gestite inline sotto tramite `isMessage`.
    // Il discriminatore è il TIPO dell'attività (FQN), non la presenza di `item.message`: il
    // payload dell'accordion può arrivare dopo (fetch on-expand), e da chiuso l'attività sarebbe
    // stata etichettata "Email" per poi diventare "Messaggio" all'apertura.
    const isMessage = item.className === getEntityFqn('Message');
    // Deep-link: l'attività target si espande SENZA animazione, così il layout è
    // stabile quando ActivityTools scrolla al commento (niente target in movimento).
    const isFocusTarget = focusItemId != null && String(item.id) === String(focusItemId);
    // Etichetta di tipo: i Message mostrano "Messaggio" pur avendo type='email' (per cadere
    // nella sezione Email); gli altri usano l'etichetta del proprio tipo.
    const typeLabel = isContributed
      ? item.title
      : isMessage
        ? t('activity.types.message')
        : t(`activity.types.${item.type}`, { defaultValue: item.type });
    const metaText = isNote || isContributed
      ? t('activity.metaBy', { user: item.user })
      : t('activity.metaAssignedTo', { user: item.user });
    // Riga 2 (titolo): per note e task e' sempre il titolo inserito; per gli altri
    // tipi (chiamata/riunione/email) "Primo contatto - {nome}" se c'e' una persona
    // linkata, altrimenti il titolo originale.
    const subjectText = isContributed
      ? item.detail ?? ''
      : isNote || item.type === 'task' || !item.firstContactName
        ? item.title
        : `${t('activity.firstContact')} - ${item.firstContactName}`;
    // Attività "descrizione-come-titolo" (email/chiamata/riunione/linkedin): da
    // chiuso, senza un "Primo contatto" linkato, mostriamo il testo ORIGINALE
    // (item.detail, non il troncato item.title) su max 2 righe con sfumatura,
    // invece del taglio netto a 80 char. Cap solo per non tenere corpi email
    // enormi nel DOM: dev'essere ABBONDANTEMENTE sopra la capacità di 2 righe a
    // qualsiasi larghezza (~400 char anche ultrawide), altrimenti su schermi
    // larghi il testo capato entrerebbe in 2 righe e la sfumatura non comparirebbe.
    const PREVIEW_MAX = 2000;
    // I Message NON usano l'anteprima-corpo-come-titolo: mostrano l'oggetto (item.title).
    const isDescriptionType =
      !isMessage &&
      (item.type === 'email' ||
        item.type === 'chiamata' ||
        item.type === 'riunione' ||
        item.type === 'linkedin' ||
        item.type === 'whatsapp');
    const showsDescriptionPreview = isDescriptionType && !item.firstContactName;
    const titleText = showsDescriptionPreview
      ? (item.detail ?? item.title).slice(0, PREVIEW_MAX)
      : subjectText;
    // Task completato: titolo barrato (vedi &--completed in SCSS).
    const completed = item.status === 'completed';
    const titleCompletedCls = completed ? ' activity-accordion__title--completed' : '';
    const showDueDate = !isNote && !!item.expirationDate;
    const dueLabel = showDueDate
      ? (item.status === 'expired'
          ? t('activity.dueLabel.expired')
          : t('activity.dueLabel.upcoming'))
      : null;
    const dateText = showDueDate
      ? formatDateTime(item.expirationDate!)
      : formatDateTime(item.date);
    return (
      <Accordion
        key={item.id}
        disableGutters
        elevation={0}
        className={`activity-accordion${item.pinned ? ' activity-accordion--pinned' : ''}`}
        expanded={expandedIds.has(item.id)}
        onChange={() => toggleOne(item.id)}
        slotProps={{ transition: { timeout: isFocusTarget ? 0 : undefined } }}
      >
        {/* `component="div"`: di default MUI rende il summary un <button> NATIVO,
            e qui dentro ci vive il titolo del task editabile (un <input>). Un
            input dentro un button non e' solo markup invalido: la barra
            spaziatrice battuta nel campo viene interpretata dal browser come
            "attivazione del bottone", quindi ogni spazio richiudeva l'accordion
            (lasciando lo sfondo grigio del Mui-focusVisible) invece di scrivere.
            Da <div> MUI gestisce Invio/Spazio in JS e attiva SOLO se il tasto
            arriva dal summary stesso (target === currentTarget), quindi il campo
            si tiene i suoi spazi. Restano role="button" + tabIndex, quindi da
            tastiera l'accordion si apre e si chiude come prima. */}
        <AccordionSummary component="div" expandIcon={<ExpandMoreIcon />}>
          {/* Badge pin: pin arancione dentro un cerchio bianco, sporge per
              meta' in alto al centro del pannello (posizionamento in SCSS). */}
          {item.pinned && (
            <span className="activity-accordion__pin-badge" aria-hidden="true">
              <PushPinIcon />
            </span>
          )}
          {/* Emblema tipo attività: icona in un box (tinta unita), nel rail
              sinistro; i box consecutivi sono uniti dalla linea connettrice. */}
          <span className="activity-accordion__emblem" aria-hidden="true">
            {TYPE_ICON[item.type]}
          </span>
          <div className="activity-accordion__summary">
            <div className="activity-accordion__line1">
              <strong className="activity-accordion__type">{typeLabel}</strong>
              <span className="activity-accordion__meta">
                {metaText}
              </span>
              {/* Menu "Azioni": visibile solo a pannello esteso, a sinistra della data. */}
              {expandedIds.has(item.id) && !isContributed && (
                <Button
                  component="div"
                  size="small"
                  variant="text"
                  className="activity-accordion__actions-btn"
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={(e) => openActions(e, item.id)}
                >
                  {t('activity.actions.label')}
                </Button>
              )}
              <span className="activity-accordion__date">
                {showDueDate && <EventIcon className="activity-accordion__date-icon" />}
                {dueLabel ? `${dueLabel}: ${dateText}` : dateText}
              </span>
            </div>
            {/* "con {contatti}" sopra al titolo: i contatti collegati. Solo da
                esteso. I contatti diversi da quello della pagina sono link
                (grassetto) al loro dettaglio; quello corrente resta testo piano. */}
            {expandedIds.has(item.id) &&
              item.recipients &&
              item.recipients.length > 0 && (
                <div className="activity-accordion__recipient text-xs text-muted">
                  {t('activity.with')}{' '}
                  {item.recipients.map((r, i) => (
                    <Fragment key={r.personId}>
                      {i > 0 && ', '}
                      {r.self ? (
                        r.name
                      ) : (
                        <Link
                          to={`/contacts/${r.personId}`}
                          className="activity-accordion__recipient-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {r.name}
                        </Link>
                      )}
                    </Fragment>
                  ))}
                </div>
              )}
            <div className="activity-accordion__line2">
              {/* Pallino di stato: per i task e' il toggle "completato" cliccabile;
                  per gli altri tipi (oggi nessuno valorizza status) resta l'icona. */}
              {item.task ? (
                <TaskCompletionToggle
                  task={item.task}
                  onToggle={() => toggleTaskCompleted(item.task!)}
                />
              ) : (
                !isNote && item.status && <TaskStatusIcon status={item.status} />
              )}
              {/* Da espanso, per i task il titolo diventa editabile inline (unico
                  titolo: il duplicato nel body e' nascosto via fieldConfigOverride).
                  stopPropagation: editare/cliccare il titolo non deve togglare il
                  pannello. Da chiuso resta il titolo readonly, identico a prima. */}
              {expandedIds.has(item.id) && item.task ? (
                <div
                  className={`activity-accordion__title activity-accordion__title--edit${titleCompletedCls}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <EditableField
                    value={item.task.title}
                    variant={{ kind: 'text' }}
                    required
                    flat
                    label={t('task.taskTitle')}
                    onCommit={(v) => commitTaskTitle(item.task!, v)}
                  />
                </div>
              ) : (
                <ClampedTitle
                  text={titleText}
                  multiline={showsDescriptionPreview}
                  completed={completed}
                  expanded={expandedIds.has(item.id)}
                />
              )}
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails className="activity-accordion__details">
          {/* Lazy: il body pesante (autocomplete, lookups) viene istanziato
              solo quando l'accordion e' espanso. */}
          {expandedIds.has(item.id) && item.task ? (
            // Titolo nascosto qui: e' editabile inline nell'header (vedi line2).
            <TaskEditFormBody task={item.task} fieldConfigOverride={{ title: { hidden: true } }} />
          ) : expandedIds.has(item.id) && item.note ? (
            <NoteEditFormBody note={item.note} />
          ) : expandedIds.has(item.id) && item.call ? (
            <CallEditFormBody call={item.call} />
          ) : expandedIds.has(item.id) && item.email ? (
            <EmailEditFormBody email={item.email} />
          ) : expandedIds.has(item.id) && item.meeting ? (
            <MeetingEditFormBody meeting={item.meeting} />
          ) : expandedIds.has(item.id) && item.linkedinPost ? (
            <LinkedinPostEditFormBody linkedinPost={item.linkedinPost} />
          ) : expandedIds.has(item.id) && item.message ? (
            <MessageActivityBody message={item.message} />
          ) : expandedIds.has(item.id) && item.whatsAppSession ? (
            <WhatsAppSessionChat session={item.whatsAppSession} />
          ) : (
            <>
              {item.detail && (
                <div className={`text-sm text-secondary${isNote ? ' callout' : ''}`}>{item.detail}</div>
              )}
              <div className="text-xs text-light mt-sm">— {item.user}</div>
            </>
          )}
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <div>
      {/* Search bar (opt-in) + toggle Comprimi/Espandi tutto allineato a destra.
          Sulla tab di un plugin restano visibili ma DISABILITATI: qui sotto non
          c'e' una lista su cui agire. Visibili perche' stanno SOPRA la striscia e
          nasconderli la farebbe scattare verso l'alto a ogni cambio di tab;
          disabilitati perche' un comando che sembra funzionante e non fa niente
          e' peggio di uno spento — spento dice "non si applica qui". Il testo
          digitato non si perde: tornando su una tab della lista torna a filtrare. */}
      {searchable && (
        <div className="activity-search-row">
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('activity.searchPlaceholder')}
            size="small"
            disabled={activeContributor != null}
            className="activity-search-row__input"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button
            size="small"
            variant="text"
            startIcon={allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            onClick={toggleAll}
            disabled={activeContributor != null || displayed.length === 0}
          >
            {allExpanded ? t('activity.collapseAll') : t('activity.expandAll')}
          </Button>
        </div>
      )}

      {/* Striscia a selezione singola, in SLUG: il valore vuoto e' "Tutte", gli
          altri sono il segmento URL della sezione. Le tab contribuite dai plugin
          stanno qui dentro, ordinate insieme alle core: non c'e' un ramo a parte
          per loro, altrimenti si comporterebbero diversamente dalle vicine. */}
      {stripTabsWithCounts.length > 0 && (
        <Tabs
          value={activeSection ?? ''}
          onChange={(_, v) => onSectionChange?.(v === '' ? undefined : (v as string))}
          // Niente `variant="scrollable"`: i chip vanno a capo (vedi
          // .activity-filter-tabs in _detail.scss), non scorrono. La variante
          // scrollable per giunta non funzionava: il design vuole
          // `.MuiTabs-scroller { overflow: visible }` — altrimenti gli angoli
          // tondi dei pill vengono tranciati — ma senza clipping MUI misura
          // scrollWidth == clientWidth, quindi non rilevava mai overflow e non
          // mostrava i bottoni di scorrimento. I chip in eccesso finivano
          // tagliati dall'overflow hidden della root, sotto la colonna destra.
          className="activity-filter-tabs"
        >
          {stripTabsWithCounts.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              className="tab-lowercase filter-chip"
              label={
                <span className="filter-chip__label">
                  {tab.label}
                  {tab.count !== null && (
                    <span className="filter-chip__count">{tab.count}</span>
                  )}
                </span>
              }
            />
          ))}
        </Tabs>
      )}

      {/* Header del contenuto del tab attivo: quando un tipo specifico
          e' selezionato (non "Tutte") e il parent fornisce getCreateAction,
          mostra il bottone "+ Nuova X" in alto a destra dell'area contenuto. */}
      {(() => {
        if (activeContributor) return null;
        const selectedType = (activeFilters && activeFilters.length === 1)
          ? (activeFilters[0] as ActivityItem['type'])
          : null;
        const action = selectedType && getCreateAction
          ? getCreateAction(selectedType)
          : null;
        if (!action) return null;
        const btn = (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.label}
          </Button>
        );
        return (
          <div className="activity-tab-header">
            {action.disabled && action.disabledReason ? (
              <Tooltip title={action.disabledReason}>
                <span>{btn}</span>
              </Tooltip>
            ) : (
              btn
            )}
          </div>
        );
      })()}

      {/* Tab di un plugin: al posto della lista si monta il suo componente. Il core
          gli passa solo l'id dell'entita' — i dati se li procura lui. */}
      {activeContributor && (
        <activeContributor.component entityId={entityId ?? 0} />
      )}

      {/* Timeline raggruppata per mese. Ogni attivita' e' un Accordion:
          summary su due righe (line1: tipo+meta+data; line2: status icon
          per task non-nota + "Primo contatto - {nome}" oppure titolo nota),
          details con descrizione e autore. */}
      {activeContributor ? null : displayed.length === 0 ? (
        <p className="text-muted">{emptyMessage ?? t('activity.empty')}</p>
      ) : (
        <div>
          {/* Sezione "Imminente": task scaduti non completati, PRIMA cosa nello
              stream — vanno visti subito. Escono di qui appena completati. */}
          {expiredItems.length > 0 && (
            <div className="activity-imminent-group">
              <h4 className="activity-month-heading activity-imminent-heading">
                {t('activity.imminentHeading')}
              </h4>
              {expiredItems.map(renderActivity)}
            </div>
          )}
          {/* Attivita' appuntate: in cima, dopo gli scaduti, fuori dai gruppi
              mese. Wrapper con padding-top per spazio stabile dai tab e room al
              badge (evita il margin-collapse sull'accordion). */}
          {pinnedItems.length > 0 && (
            <div className="activity-pinned-group">
              {pinnedItems.map(renderActivity)}
            </div>
          )}
          {monthGroups.map((group) => (
            <div key={group.label}>
              <h4 className="activity-month-heading">{group.label}</h4>
              {group.items.map(renderActivity)}
            </div>
          ))}
        </div>
      )}

      {/* Menu azioni preimpostate sull'entita' del pannello. Le voci sono
          stub (per ora non eseguono l'azione): Appunta/Sblocca gestira' il
          pinning, Elimina la rimozione del record. */}
      <Menu
        anchorEl={actionsMenu?.anchorEl ?? null}
        open={Boolean(actionsMenu)}
        onClose={closeActions}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {/* Appunta/Sblocca: toggle reale del pin (TS-22). Label e icona
            riflettono lo stato corrente dell'item attivo. */}
        <MenuItem onClick={() => void handlePin()}>
          <ListItemIcon>
            {activePinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
          </ListItemIcon>
          {activePinned ? t('activity.actions.unpin') : t('activity.actions.pin')}
        </MenuItem>
        {/* Elimina non disponibile per i Message: sono email importate, non si cancellano
            dalla timeline (solo Appunta). */}
        {!activeItem?.message && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            {t('activity.actions.delete')}
          </MenuItem>
        )}
      </Menu>

      {/* Alert "una sola attivita' appuntata per contatto": compare quando si
          appunta una nuova attivita' mentre ne esiste gia' un'altra pinnata.
          L'utente sceglie se sostituire (conferma) o mantenere l'attuale. */}
      <ConfirmDialog
        open={pinConfirm.isOpen}
        title={t('activity.actions.pinReplaceTitle')}
        message={pinConfirm.message}
        onConfirm={pinConfirm.confirm}
        onCancel={pinConfirm.cancel}
        confirmLabel={t('activity.actions.pinReplaceConfirm')}
        cancelLabel={t('activity.actions.pinReplaceCancel')}
        confirmColor="primary"
      />

      {/* Conferma eliminazione attivita' (azione distruttiva → bottone error). */}
      <ConfirmDialog
        open={deleteConfirm.isOpen}
        message={deleteConfirm.message}
        onConfirm={deleteConfirm.confirm}
        onCancel={deleteConfirm.cancel}
        confirmLabel={t('activity.actions.delete')}
      />
    </div>
  );
}
